import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import logo from '../assets/photos/hlthIQ.png';
import '../assets/css/Research.css';

const ResearchHeader = ({ 
  toggleSidebar, 
  isSidebarOpen, 
  isSidebarCollapsed, 
  pageTitle, 
  user, 
  isMobile, 
  className,
  onOpenSystemSettings // Add this prop for opening system settings
}) => {
  const { logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  // State management
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  
  // Refs for handling click outside
  const userMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const searchInputRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserMenu && userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
      
      if (showMobileMenu && mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setShowMobileMenu(false);
      }
      
      if (showNotifications && notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserMenu, showMobileMenu, showNotifications]);

  // Focus search input when opened
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  // Load notifications only when user opens the notifications panel (on demand)
  const loadNotifications = async () => {
    if (!user || (user.role !== 'medical_admin' && user.role !== 'program_admin')) {
      return;
    }
    
    // Don't reload if already loaded and panel is open
    if (showNotifications && notifications.length > 0) {
      return;
    }
    
    try {
      setLoadingNotifications(true);
      const response = await api.get('/reports/notifications');
      if (response.data && response.data.success) {
        setNotifications(response.data.notifications || []);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
      // Keep existing notifications on error
    } finally {
      setLoadingNotifications(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    console.log('Searching for:', searchQuery);
    // Add your search functionality here
    setShowSearch(false);
  };

  // Mark all notifications as read
  const markAllNotificationsAsRead = () => {
    setNotifications(notifications.map(notification => ({
      ...notification,
      isRead: true
    })));
  };

  // Mark a specific notification as read
  const markNotificationAsRead = (id) => {
    setNotifications(notifications.map(notification => 
      notification.id === id ? { ...notification, isRead: true } : notification
    ));
  };

  // Count unread notifications
  const unreadNotificationsCount = notifications.filter(n => !n.isRead).length;

  // Get role display name
  const getRoleDisplay = (role) => {
    if (!role) return 'User';
    
    const roleMap = {
      'doctor': 'Doctor',
      'medical_admin': 'Medical Admin',
      'program_admin': 'Program Admin',
      'statistician': 'Statistician'
    };
    
    return roleMap[role] || role.replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get role badge styling
  const getRoleBadgeClass = (role) => {
    const badgeMap = {
      'doctor': 'role-badge-success',
      'medical_admin': 'role-badge-primary',
      'program_admin': 'role-badge-warning',
      'statistician': 'role-badge-info'
    };
    
    return badgeMap[role] || 'role-badge-secondary';
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
  };

  // Get header class name
  const getHeaderClassName = () => {
    let classes = 'research-header';
    if (className) classes += ` ${className}`;
    if (isMobile) {
      if (!isSidebarOpen) classes += ' sidebar-closed';
    } else {
      if (isSidebarCollapsed) classes += ' sidebar-collapsed';
    }
    return classes;
  };

  // Handle system settings click
  const handleSystemSettingsClick = () => {
    // Close menus
    setShowUserMenu(false);
    setShowMobileMenu(false);
    
    // Call the provided callback
    if (onOpenSystemSettings) {
      onOpenSystemSettings();
    }
  };

  return (
    <header className={getHeaderClassName()}>
      {/* Main Header Container */}
      <div className="header-container">
        {/* Sidebar Toggle Button */}
        <button
          className="sidebar-toggle-btn"
          onClick={toggleSidebar}
          aria-label={isMobile ? (isSidebarOpen ? "Close sidebar" : "Open sidebar") : (isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar")}
        >
          <i className={`fas ${isMobile ? (isSidebarOpen ? 'fa-times' : 'fa-bars') : (isSidebarCollapsed ? 'fa-angle-right' : 'fa-angle-left')}`}></i>
        </button>

        {/* Brand Section */}
        <div className="header-brand">
          <div className="header-logo-container">
            <img src={logo} alt="HealthIQ Research" className="header-logo" />
          </div>
          <div className="header-title-container">
            <h1 className="header-title">Research Platform</h1>
            {pageTitle && <p className="header-subtitle">{pageTitle}</p>}
          </div>
        </div>

        {/* Search Button (Show only on desktop) */}
        <div className="header-search-trigger">
          <button 
            className="search-trigger-btn" 
            onClick={() => setShowSearch(!showSearch)}
            aria-label="Search"
          >
            <i className="fas fa-search"></i>
          </button>
          
          {/* Search Overlay */}
          {showSearch && (
            <div className="search-overlay">
              <form onSubmit={handleSearch} className="search-form">
                <input
                  type="text"
                  ref={searchInputRef}
                  placeholder="Search protocols, cases, doctors..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button type="submit" className="search-submit">
                  <i className="fas fa-search"></i>
                </button>
                <button 
                  type="button" 
                  className="search-close"
                  onClick={() => setShowSearch(false)}
                >
                  <i className="fas fa-times"></i>
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Header Actions */}
        <div className="header-actions">
          {/* System Settings Button - Added as a standalone button */}
          <button 
            className="system-settings-btn"
            onClick={handleSystemSettingsClick}
            aria-label="System Settings"
            title="System Settings"
          >
            <i className="fas fa-cogs"></i>
          </button>

          {/* Notifications */}
          <div className="header-notifications" ref={notificationsRef}>
            <button 
              className="notification-btn"
              onClick={() => {
                setShowNotifications(!showNotifications);
                if (!showNotifications) {
                  loadNotifications();
                }
              }}
              aria-label="Notifications"
            >
              <i className="fas fa-bell"></i>
              {unreadNotificationsCount > 0 && (
                <span className="notification-badge">{unreadNotificationsCount}</span>
              )}
            </button>
            
            {showNotifications && (
              <div className="notifications-panel">
                <div className="notifications-header">
                  <h3>Notifications</h3>
                  <button 
                    className="mark-all-read"
                    onClick={markAllNotificationsAsRead}
                  >
                    Mark all read
                  </button>
                </div>
                
                <div className="notifications-body">
                  {loadingNotifications ? (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      <p className="mt-2 mb-0 small">Loading notifications...</p>
                    </div>
                  ) : notifications.length > 0 ? (
                    <div className="notifications-list">
                      {notifications.map(notification => (
                        <div 
                          key={notification.id} 
                          className={`notification-item ${notification.isRead ? 'read' : 'unread'}`}
                        >
                          <div className={`notification-icon ${notification.type}`}>
                            <i className={`fas ${
                              notification.type === 'protocol' ? 'fa-clipboard-list' :
                              notification.type === 'case' ? 'fa-user-md' : 
                              'fa-cog'
                            }`}></i>
                          </div>
                          <div className="notification-content">
                            <p className="notification-message">{notification.message}</p>
                            <span className="notification-time">{notification.time}</span>
                          </div>
                          {!notification.isRead && (
                            <button 
                              className="notification-read-btn"
                              onClick={() => markNotificationAsRead(notification.id)}
                              aria-label="Mark as read"
                            >
                              <i className="fas fa-circle"></i>
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="no-notifications">
                      <i className="fas fa-check-circle"></i>
                      <p>All caught up!</p>
                    </div>
                  )}
                </div>
                
                <div className="notifications-footer">
                  <Link to="#">View all notifications</Link>
                </div>
              </div>
            )}
          </div>

          {/* Theme Toggle */}
          <button 
            className="theme-toggle-btn"
            onClick={toggleTheme}
            aria-label={`Switch to ${isDarkMode ? 'light' : 'dark'} mode`}
          >
            <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}></i>
          </button>

          {/* User Menu */}
          <div className="user-menu-container" ref={userMenuRef}>
            <button 
              className="user-menu-trigger"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-expanded={showUserMenu}
            >
              {user?.avatar ? (
                <img 
                  src={user.avatar} 
                  alt={user?.name || "User"} 
                  className="user-avatar"
                />
              ) : (
                <div className="user-avatar-placeholder">
                  {getInitials(user?.name)}
                </div>
              )}
              <div className="user-info">
                <span className="user-name">{user?.name || "User"}</span>
                <span className={`user-role ${getRoleBadgeClass(user?.role)}`}>
                  {getRoleDisplay(user?.role)}
                </span>
              </div>
              <i className={`fas fa-chevron-${showUserMenu ? 'up' : 'down'}`}></i>
            </button>
            
            {showUserMenu && (
              <div className="user-dropdown">
                <div className="user-dropdown-header">
                  <div className="user-dropdown-info">
                    <span className="user-dropdown-name">{user?.name || "User"}</span>
                    <span className="user-dropdown-email">{user?.email || ""}</span>
                  </div>
                </div>
                <div className="user-dropdown-body">
                  <Link to="#" className="user-dropdown-item">
                    <i className="fas fa-user"></i>
                    <span>Profile Settings</span>
                  </Link>

                  {/* Add System Settings to dropdown */}
                  <button className="user-dropdown-item" onClick={handleSystemSettingsClick}>
                    <i className="fas fa-cogs"></i>
                    <span>System Settings</span>
                  </button>

                  <Link to="#" className="user-dropdown-item">
                    <i className="fas fa-image"></i>
                    <span>Logo Management</span>
                  </Link>
                  <button className="user-dropdown-item" onClick={toggleTheme}>
                    <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}></i>
                    <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                  </button>
                  <div className="user-dropdown-divider"></div>
                  <button className="user-dropdown-item logout" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className="mobile-menu-toggle"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            aria-label="Mobile menu"
          >
            <i className="fas fa-ellipsis-v"></i>
          </button>
          
          {/* Mobile Menu */}
          {showMobileMenu && (
            <div className="mobile-menu-wrapper" ref={mobileMenuRef}>
              <div className="mobile-menu">
                <div className="mobile-menu-header">
                  <span>Menu</span>
                  <button 
                    onClick={() => setShowMobileMenu(false)}
                    aria-label="Close menu"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="mobile-menu-body">
                  <button className="mobile-menu-item" onClick={toggleTheme}>
                    <i className={`fas ${isDarkMode ? 'fa-sun' : 'fa-moon'}`}></i>
                    <span>{isDarkMode ? 'Light Mode' : 'Dark Mode'}</span>
                  </button>
                  <Link to="/profile" className="mobile-menu-item">
                    <i className="fas fa-user"></i>
                    <span>Profile Settings</span>
                  </Link>

                  {/* Add System Settings to mobile menu */}
                  <button className="mobile-menu-item" onClick={handleSystemSettingsClick}>
                    <i className="fas fa-cogs"></i>
                    <span>System Settings</span>
                  </button>

                  <Link to="/settings/logo" className="mobile-menu-item">
                    <i className="fas fa-image"></i>
                    <span>Logo Management</span>
                  </Link>
                  <Link to="/notifications" className="mobile-menu-item">
                    <i className="fas fa-bell"></i>
                    <span>Notifications</span>
                    {unreadNotificationsCount > 0 && (
                      <span className="mobile-badge">{unreadNotificationsCount}</span>
                    )}
                  </Link>
                  <div className="mobile-menu-divider"></div>
                  <button className="mobile-menu-item logout" onClick={handleLogout}>
                    <i className="fas fa-sign-out-alt"></i>
                    <span>Logout</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default ResearchHeader;